import type { FieldKind } from "../extensions/mintlify/fields-html"
import {
	DEFAULT_EXPANDABLE_TITLE,
	expandableJsxAttributes,
	FIELD_DATA_TYPE,
	FIELD_TAG,
	fieldJsxAttributes,
	readFieldAttributes,
} from "../extensions/mintlify/fields-html"

interface TurndownLike {
	addRule: (
		key: string,
		rule: {
			filter: (node: Node) => boolean
			replacement: (content: string, node: Node) => string
		},
	) => void
}

const indent = (block: string): string =>
	block
		.split("\n")
		.map((line) => (line.trim() ? `  ${line}` : line))
		.join("\n")

const element = (node: Node): HTMLElement | null =>
	node.nodeType === 1 ? (node as HTMLElement) : null

const fieldKind = (node: Node): FieldKind | null => {
	const type = element(node)?.getAttribute("data-type")
	if (type === FIELD_DATA_TYPE.param) return "param"
	if (type === FIELD_DATA_TYPE.response) return "response"
	return null
}

const hasClass = (node: Node, className: string): boolean =>
	element(node)?.classList.contains(className) ?? false

const block = (tag: string, attributes: string, body: string): string => {
	const content = body.trim()
	return content
		? `\n\n<${tag} ${attributes}>\n${indent(content)}\n</${tag}>\n\n`
		: `\n\n<${tag} ${attributes} />\n\n`
}

/**
 * Writes ParamField, ResponseField and Expandable as Mintlify's own MDX tags.
 * Register after any rule that keeps a bare <details> as raw HTML, so the
 * expandable wins: turndown tries the newest rule first.
 */
export function addFieldsMarkdownRules(turndown: TurndownLike): void {
	turndown.addRule("mintlifyField", {
		filter: (node) => fieldKind(node) !== null,
		replacement: (content, node) => {
			const kind = fieldKind(node) ?? "response"
			const attrs = readFieldAttributes(kind, node as HTMLElement)
			return block(FIELD_TAG[kind], fieldJsxAttributes(kind, attrs), content)
		},
	})

	// The header only repeats the attributes, which the tag already carries.
	turndown.addRule("mintlifyFieldHead", {
		filter: (node) => hasClass(node, "pl-field-head"),
		replacement: () => "",
	})

	turndown.addRule("mintlifyExpandable", {
		filter: (node) =>
			node.nodeName === "DETAILS" &&
			element(node)?.getAttribute("data-type") === "expandable",
		replacement: (content, node) => {
			const el = node as HTMLElement
			const title = el.getAttribute("data-title") ?? DEFAULT_EXPANDABLE_TITLE
			return block(
				"Expandable",
				expandableJsxAttributes(title, el.hasAttribute("open")),
				content,
			)
		},
	})

	turndown.addRule("mintlifyExpandableSummary", {
		filter: (node) => hasClass(node, "pl-expandable-summary"),
		replacement: () => "",
	})
}
